import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "./apiClient";

import cookiesHelper from "../helpers/cookies";

import { HMIDeleteReport, HMIUploadReport, IAssessmentListItemForAdmin } from "../interfaces/services";

import useToast from "./useToast";

export const useGetAssessments = (organizationId: number) => {
    const queryFn = async (): Promise<IAssessmentListItemForAdmin[]> => {
        const token = cookiesHelper.getCookie(`${process.env.REACT_APP_AUTH_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get(`assessments/admin/${organizationId}`);
        return response.data;
    };

    return useQuery({
        queryKey: ['assessments', organizationId],
        queryFn,
        retry: 0,
        enabled: !!organizationId
    });
};

export const useUploadReport = () => {
    const { addToast } = useToast();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIUploadReport) => {
        const token = cookiesHelper.getCookie(`${process.env.REACT_APP_AUTH_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.patch(
            `assessments/upload-report/${data.id}`,
            { file: data.file },
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsReportUploading(true);
        },
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Report uploaded successfully.',
            });
            queryClient.invalidateQueries({ queryKey: ['assessments'] });
        },
        onError: (error: any, variables) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsReportUploading(false);
        },
    });
};

export const useDeleteReport = () => {
    const { addToast } = useToast();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIDeleteReport) => {
        const token = cookiesHelper.getCookie(`${process.env.REACT_APP_AUTH_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.delete(`assessments/delete-report/${data.id}`);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsReportDeleting(true);
        },
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Report deleted successfully.',
            });
            queryClient.invalidateQueries({ queryKey: ['assessments'] });
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsReportDeleting(false);
        },
    });
};